import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrainFront, Utensils, TriangleAlert, GripVertical, Search, Plus, X, Undo2,
  ArrowRight, Sunrise, Sun, Moon, ChevronLeft, ChevronRight, Hand,
} from "lucide-react";
import BrandHeader from "../components/BrandHeader";
import ViewSwitch from "../components/ViewSwitch";
import { placeKey } from "../utils/agenda";

/*
 * The agenda — the step between choosing places and writing the itinerary (owner's idea, 2026-10-07).
 * The brain's plan becomes a calendar the traveller can shape by hand: move a place to another day or
 * part of the day, take it out, add one from the catalog. Every change is re-timed at once — opening
 * hours, the time each visit takes, the ride between areas — so an overloaded day shows before anything
 * is written. When it feels right, "Generate itinerary" sends the agenda to be written.
 *
 * Two views of one trip (owner, 2026-11-04): "Your trip at a glance" (the main one) says WHAT is in the trip, this
 * calendar says WHEN. Taking a place out here takes it out of the glance (onTakeOut); bringing one from the catalog
 * adds it there (onBring); what the glance chose that the days do not hold yet waits here as "to place".
 *
 * PROTOTYPE: fed with sample data (agendaDemoData.js); not wired to the backend. The real step will
 * receive the same shape from the preview and send the result as calendar_choices
 * (backend core/calendar_draft + core/calendar_generation).
 */

const PARTS = {
  morning: { label: "Morning", icon: Sunrise },
  lunch: { label: "Lunch", icon: Utensils },
  afternoon: { label: "Afternoon", icon: Sun },
  dinner: { label: "Dinner", icon: Utensils },
  evening: { label: "Evening", icon: Moon },
  travel: { label: "On the move", icon: TrainFront },
};
const ORDER = ["morning", "lunch", "afternoon", "dinner", "evening"];
const CAT_COLOR = {
  culture: "var(--cat-culture-solid)", food: "var(--cat-foodie-solid)", nightlife: "var(--cat-nightlife-solid)",
  special: "var(--cat-special-solid)", popular: "var(--cat-popular-solid)", hidden_gem: "var(--cat-hidden-solid)",
};
const CAT_NAME = { culture: "Culture", food: "Food", nightlife: "Nightlife", special: "Special", popular: "Popular", hidden_gem: "Hidden gem" };
const DAY_END = "22:30";
const FLOOR = { afternoon: 780, evening: 1020 };       // the earliest a part of the day begins
const WEEK = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// ── time helpers ────────────────────────────────────────────────────────────
const toMin = (hhmm) => {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const hm = (mins) => {
  const m = Math.round(mins);
  const h = Math.floor(m / 60) % 24;
  return `${String(h).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};
const dur = (mins) => {
  const m = Math.round(mins);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};
const windows = (text) => {
  if (text == null) return null;                       // unknown: no wall to check
  if (/closed/i.test(text)) return [];
  return text.split(",").map((part) => {
    const [a, b] = part.split("-").map((s) => toMin(s.trim()));
    if (a == null || b == null) return null;
    return [a, b <= a ? b + 1440 : b];
  }).filter(Boolean);
};
const dayLabel = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};
const km = (a, b) => {
  if (!a?.lat || !b?.lat) return null;
  const p = Math.PI / 180;
  const x = Math.sin(((b.lat - a.lat) * p) / 2) ** 2
    + Math.cos(a.lat * p) * Math.cos(b.lat * p) * Math.sin(((b.lng - a.lng) * p) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(x));
};
// a coarse ride estimate for the prototype; the real step asks the server (core/local_transit)
const ride = (a, b) => {
  const d = km(a, b);
  if (d == null || d < 0.05) return { minutes: 0, method: "walk" };
  if (d <= 1.2) return { minutes: Math.round(d * 14) + 2, method: "walk" };
  return { minutes: Math.min(120, Math.round(10 + d * 2.2)), method: "train or bus" };
};
const openOn = (place, iso) => {
  if (place.openWeek) return place.openWeek[WEEK[new Date(`${iso}T12:00:00`).getDay()]] ?? null;
  return place.open ?? null;
};

// ── the brain's plan → editable parts ───────────────────────────────────────
function toAgenda(data) {
  return data.days.map((d) => {
    const parts = [];
    for (const b of d.blocks) {
      const last = parts[parts.length - 1];
      if (b.kind === "travel") {
        const departure = /KIX|NRT|HND|airport/i.test(b.text);
        parts.push({ key: "travel", label: b.label, text: b.text, minutes: departure ? 150 : 180, departure });
      } else if (b.kind === "lunch" || b.kind === "dinner") {
        if (last && last.key === b.kind) last.options.push(...b.options);
        else parts.push({ key: b.kind, options: [...b.options] });
      } else if (last && last.key === b.kind) {
        last.places.push(...b.places);
      } else {
        parts.push({ key: b.kind, places: [...b.places] });
      }
    }
    const firstTime = d.blocks.flatMap((b) => (b.times || []).map((t) => toMin(t[0]))).find((t) => t != null);
    const start = Math.min(firstTime ?? 480, d.n === 1 ? 1050 : 480);
    // a day without a train keeps every part of the day open to receive a place — from the hour it begins
    // (an arrival at 17:00 has no morning to fill)
    const begins = { morning: 0, lunch: 690, afternoon: 810, dinner: 1080, evening: 1050 };
    const after = (key) => start < (begins[key] === 0 ? 720 : begins[key] + 180);
    if (!parts.some((p) => p.key === "travel")) {
      for (const key of ORDER.filter(after)) {
        if (!parts.some((p) => p.key === key)) {
          const at = parts.findIndex((p) => ORDER.indexOf(p.key) > ORDER.indexOf(key));
          const part = key === "lunch" || key === "dinner" ? { key, options: [] } : { key, places: [] };
          if (at < 0) parts.push(part); else parts.splice(at, 0, part);
        }
      }
    }
    return { n: d.n, date: d.date, city: d.city, title: d.title, start, parts };
  });
}

// ── timing: one pass over the day, the same order the traveller reads it ───
function schedule(day) {
  const end = toMin(DAY_END);
  let cursor = day.start, prev = null, ended = false;
  let visits = 0, transit = 0, meals = 0;
  const notes = [];
  const parts = day.parts.map((part) => {
    if (part.key === "travel") {
      const at = cursor;
      if (part.departure) { ended = true; return { ...part, at, until: at + part.minutes }; }
      cursor += part.minutes; prev = null;
      return { ...part, at, until: cursor };
    }
    if (part.key === "lunch" || part.key === "dinner") {
      if (ended || (!part.options.length && !part.chosen)) return { ...part, at: null };
      const [from, late] = part.key === "lunch" ? [690, 870] : [1080, 1260];
      const at = Math.max(cursor, from), length = part.key === "lunch" ? 60 : 75;
      const warn = at > late ? `${part.key === "lunch" ? "Lunch" : "Dinner"} at ${hm(at)} — most kitchens are closing` : null;
      if (warn) notes.push(warn);
      cursor = at + length; meals += length;
      return { ...part, at, until: cursor, warn };
    }
    // what they put in the afternoon is not done at eleven, the evening not at three
    if (part.places.length) cursor = Math.max(cursor, FLOOR[part.key] || 0);
    const places = part.places.map((p) => {
      const leg = prev ? ride(prev, p) : { minutes: 0, method: "walk" };
      const elsewhere = prev && p.city && prev.city && p.city !== prev.city;
      let start = cursor + (elsewhere ? 0 : leg.minutes);
      const warns = [], soft = [];
      if (ended) warns.push("After your departure");
      if (elsewhere) warns.push(`This place is in ${p.city}`);
      const wins = windows(openOn(p, day.date));
      if (wins && !wins.length) warns.push("Closed this day");
      else if (wins) {
        const fit = wins.find(([s, e]) => Math.max(start, s) + p.minutes <= e);
        if (fit) start = Math.max(start, fit[0]);
        else warns.push(`Not open at ${hm(start)} (${wins.map(([s, e]) => `${hm(s)}–${hm(e)}`).join(", ")})`);
      }
      const best = windows(p.best);
      if (best && best.length && !warns.length && !best.some(([s, e]) => start >= s - 15 && start <= e + 15)) {
        soft.push(`At its best ${best.map(([s, e]) => `${hm(s)}–${hm(e)}`).join(", ")}`);
      }
      const showLeg = prev && !elsewhere && (leg.minutes >= 10 || (p.area && prev.area && p.area !== prev.area));
      transit += elsewhere ? 0 : leg.minutes; visits += p.minutes;
      cursor = start + p.minutes;
      warns.forEach((w) => notes.push(`${p.name}: ${w}`));
      prev = p;
      return { ...p, start, finish: cursor, leg: showLeg ? leg : null, warns, soft };
    });
    return { ...part, places };
  });
  const over = cursor - end;
  if (over > 0 && !ended) notes.push(`The day runs ${dur(over)} past ${DAY_END}`);
  const used = visits + transit + meals, room = end - day.start;
  const load = used / room;
  const status = over > 0 || load > 1 ? "over" : load > 0.82 ? "full" : "easy";
  return { parts, visits, transit, meals, load, status, notes, count: day.parts.reduce((n, p) => n + (p.places?.length || 0), 0) };
}

const STATUS = {
  easy: { label: "Comfortable", color: "var(--cat-culture-solid)" },
  full: { label: "Full", color: "var(--cat-foodie-solid)" },
  over: { label: "Overloaded", color: "var(--zen-accent)" },
};

// ── the calendar: hours down, days across, everything on one screen (owner and partner, 2026-11-03) ──
// Like the Mac's or Outlook's calendar: the whole day at a glance, very little on each place, no sideways scroll.
// A place is dragged to another day or hour; a place of another city is refused; an overloaded day is allowed, and said.
const HOUR = 46;                                         // the height of one hour
const COL_MIN = 132;                                     // narrower than this, the next day goes to the next page
const AXIS = 44;
// the hours shown: from 07:00 to 23:00, or from an hour before the trip's earliest visit (Fushimi Inari at dawn)
function scaleFor(timed) {
  const starts = [], ends = [];
  timed.forEach((t) => t.parts.forEach((part) => {
    if (part.at != null) { starts.push(part.at); ends.push(part.until); }
    (part.places || []).forEach((p) => { starts.push(p.start); ends.push(p.finish); });
  }));
  const from = Math.max(4 * 60, Math.min(7 * 60, Math.floor((Math.min(...starts, 7 * 60) - 30) / 60) * 60));
  const to = Math.min(26 * 60, Math.max(23 * 60, Math.ceil((Math.max(...ends, 23 * 60)) / 60) * 60));
  const y = (m) => ((Math.min(Math.max(m, from), to) - from) / 60) * HOUR;
  return { from, to, y, height: y(to) };
}
const partFor = (mins) => (mins < 720 ? "morning" : mins < 1020 ? "afternoon" : "evening");

// the cities of the day in order: a travel day reads "Tokyo → Kyoto"
const cityLine = (day) => {
  const seen = [];
  day.parts.forEach((p) => (p.places || []).forEach((x) => { if (x.city && seen[seen.length - 1] !== x.city) seen.push(x.city); }));
  if (day.parts.some((p) => p.key === "travel" && !p.departure) && !seen.includes(day.city)) seen.push(day.city);
  return seen.length > 1 ? seen.join(" → ") : day.city;
};
const citiesOf = (day) => cityLine(day).split(" → ");
const fits = (place, day) => !place?.city || citiesOf(day).includes(place.city);

// where a drop at this hour lands: the part of the day it falls in (or the nearest one the day has), in time order
function slotFor(day, t, mins) {
  const open = day.parts.map((p, i) => (p.places ? i : -1)).filter((i) => i >= 0);
  if (!open.length) return null;
  const want = ORDER.indexOf(partFor(mins));
  const part = open.reduce((best, i) => (
    Math.abs(ORDER.indexOf(day.parts[i].key) - want) < Math.abs(ORDER.indexOf(day.parts[best].key) - want) ? i : best), open[0]);
  const index = (t.parts[part].places || []).filter((p) => p.start < mins).length;
  return { part, index };
}

// ── page ────────────────────────────────────────────────────────────────────
export default function AgendaPage({ data, onBack, onGenerate, saved, onSave, selected, listed, toPlace = [], onTakeOut, onBring }) {
  const initial = useMemo(() => toAgenda(data), [data]);
  const [days, setDays] = useState(saved?.days || initial);
  const [tray, setTray] = useState(saved?.tray || []);     // taken out of the days
  // kept by the app between the two views, so coming back finds the days as they were left
  useEffect(() => { onSave?.({ days, tray }); }, [days, tray]); // eslint-disable-line react-hooks/exhaustive-deps
  // what was taken out on the glance leaves the days too; what was chosen again there leaves "taken out"
  // (a place on the glance is `listed`; `selected` is what the glance holds now)
  useEffect(() => {
    if (!selected || !listed) return;
    const gone = (p) => listed.has(placeKey(p)) && !selected.has(placeKey(p));
    const out = days.flatMap((d) => d.parts.flatMap((part) => (part.places || []).filter(gone)));
    const back = tray.filter((p) => selected.has(placeKey(p)));
    if (!out.length && !back.length) return;
    setDays(days.map((d) => ({ ...d, parts: d.parts.map((part) => (part.places ? { ...part, places: part.places.filter((x) => !gone(x)) } : part)) })));
    setTray([...tray.filter((p) => !selected.has(placeKey(p))), ...out]);
  }, [selected, listed]); // eslint-disable-line react-hooks/exhaustive-deps
  const [menu, setMenu] = useState(null);                  // { place, from } — tap to move
  const [dragging, setDragging] = useState(null);          // the place in the hand: the days it fits light up
  const [over, setOver] = useState(null);                  // the day under it, or "tray"
  const [bounce, setBounce] = useState(null);              // why a drop was refused
  const [notesOf, setNotesOf] = useState(null);            // the day whose warnings are open
  const [addTo, setAddTo] = useState(null);                // the day we add a place to, from our catalog
  const [mealOf, setMealOf] = useState(null);              // { day, part } — where they eat, chosen from our tables
  const [drawer, setDrawer] = useState(false);             // mobile: add places
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(7);
  const [moved, setMoved] = useState(false);               // the first move done: the invitation can go
  const drag = useRef(null);
  const grid = useRef(null);
  const bounceTimer = useRef(null);

  // as many days as fit the width; the rest on the next page — never a sideways scroll
  useEffect(() => {
    const el = grid.current;
    if (!el) return undefined;
    const fit = () => setPerPage(Math.max(1, Math.min(days.length, Math.floor((el.clientWidth - AXIS) / COL_MIN))));
    fit();
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    return () => watch.disconnect();
  }, [days.length]);
  const pages = Math.max(1, Math.ceil(days.length / perPage));
  const current = Math.min(page, pages - 1);
  const shown = days.slice(current * perPage, current * perPage + perPage).map((_, k) => current * perPage + k);

  const timed = useMemo(() => days.map(schedule), [days]);
  const scale = useMemo(() => scaleFor(timed), [timed]);
  const placedIds = useMemo(() => new Set(days.flatMap((d) => d.parts.flatMap((p) => (p.places || []).map((x) => x.id)))), [days]);
  const waiting = new Set(toPlace.map(placeKey));
  const catalog = data.catalog.filter((c) => !placedIds.has(c.id) && !tray.some((t) => t.id === c.id) && !waiting.has(placeKey(c)));
  const attention = timed.filter((t) => t.status === "over" || t.notes.length).length;

  // one move for every gesture: drag, tap-to-move, remove, add
  const move = (picked, from, to) => {
    // one brought from the catalog is theirs from now on: the build keeps it as a place they added
    const place = from === "catalog" ? { ...picked, added: true } : picked;
    // the glance follows: out of the days and out of the trip, or into the trip
    if (to === "tray" && from !== "tray" && selected?.has(placeKey(place))) onTakeOut?.(place);
    if (to && to !== "tray" && (from === "catalog" || (from === "tray" && listed?.has(placeKey(place))))) onBring?.(place);
    const next = days.map((d) => ({ ...d, parts: d.parts.map((p) => (p.places ? { ...p, places: [...p.places] } : p)) }));
    if (from?.day != null) next[from.day].parts[from.part].places.splice(from.index, 1);
    let nextTray = from === "tray" ? tray.filter((t) => t.id !== place.id) : tray;
    if (to === "tray") {
      if (!nextTray.some((t) => t.id === place.id)) nextTray = [...nextTray, place];
    } else if (to) {
      const list = next[to.day].parts[to.part].places;
      let at = to.index ?? list.length;
      if (from?.day === to.day && from.part === to.part && from.index < at) at -= 1;
      list.splice(at, 0, place);
    }
    setDays(next); setTray(nextTray); setMenu(null); setMoved(true);
  };

  const chooseTable = ({ day, part }, table) => {
    setDays(days.map((d, di) => (di !== day ? d : { ...d, parts: d.parts.map((p, pi) => (pi !== part ? p : { ...p, chosen: table })) })));
    setMealOf(null); setMoved(true);
  };

  const refuse = (place, day) => {
    clearTimeout(bounceTimer.current);
    setBounce(`${place.name} is in ${place.city}, and Day ${day.n} is in ${citiesOf(day).join(" and ")}. Drop it on a ${place.city} day.`);
    bounceTimer.current = setTimeout(() => setBounce(null), 4000);
  };
  const startDrag = (place, from) => (e) => {
    drag.current = { place, from };
    setDragging(place);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", place.name);
  };
  const endDrag = () => { drag.current = null; setDragging(null); setOver(null); };
  const dropOnDay = (di) => (e) => {
    e.preventDefault();
    const hand = drag.current;
    endDrag();
    if (!hand) return;
    const day = days[di];
    if (!fits(hand.place, day)) return refuse(hand.place, day);
    const mins = scale.from + ((e.clientY - e.currentTarget.getBoundingClientRect().top) / HOUR) * 60;
    const slot = slotFor(day, timed[di], mins);
    if (slot) move(hand.place, hand.from, { day: di, ...slot });
  };
  const dropOnTray = (e) => {
    e.preventDefault();
    const hand = drag.current;
    endDrag();
    if (hand) move(hand.place, hand.from, "tray");
  };
  const reset = () => { setDays(initial); setTray([]); };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--zen-bg)", color: "var(--zen-text)" }}>
      <BrandHeader left={data.demo ? <span className="font-mono-label hidden sm:inline" style={{ fontSize: 10, color: "var(--zen-dim)" }}>Prototype · sample trip</span> : null} />

      <div className="px-4 md:px-8 pt-6 pb-3 max-w-[1600px] w-full mx-auto">
        {onBack && <div className="mb-5"><ViewSwitch view="calendar" onGlance={onBack} /></div>}
        <div className="flex items-end justify-between gap-x-6 gap-y-3 flex-wrap">
          <div>
            <p className="font-mono-label mb-1" style={{ fontSize: 10, color: "var(--zen-accent)" }}>Your days</p>
            <h1 className="font-heading text-2xl md:text-3xl tracking-tight">{data.trip?.title || "Shape your days"}</h1>
          </div>
          <Hint strong={!moved} />
        </div>
        {(data.notes || []).length > 0 && (
          <details className="mt-3 text-[12px]" data-testid="agenda-notes" style={{ color: "var(--zen-sub)" }}>
            <summary className="cursor-pointer select-none" style={{ color: "var(--zen-text)" }}>
              {data.notes.length} note{data.notes.length > 1 ? "s" : ""} from us about these days
            </summary>
            <div className="mt-2 rounded-lg px-3 py-2 space-y-1" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }}>
              {data.notes.map((n, k) => <p key={k} className="leading-snug">{n}</p>)}
            </div>
          </details>
        )}
      </div>

      <div className="flex-1 flex gap-5 px-4 md:px-8 max-w-[1600px] w-full mx-auto pb-28">
        {/* side panel (desktop): our catalog, and what was taken out */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-4">
            <Shelf tray={tray} catalog={catalog} toPlace={toPlace} startDrag={startDrag} endDrag={endDrag} onDrop={dropOnTray} over={over === "tray"}
              setOver={(o) => setOver(o)} onPick={(place, from) => setMenu({ place, from })} />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          {/* the pages of days: arrows and the days themselves, never a sideways scroll */}
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => setPage(Math.max(0, current - 1))} disabled={current === 0} aria-label="Earlier days"
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center disabled:opacity-25"
              style={{ border: "1px solid var(--zen-border)" }}><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex-1 flex flex-wrap gap-1.5 justify-center">
              {days.map((d, i) => (
                <button key={d.n} onClick={() => setPage(Math.floor(i / perPage))}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap"
                  style={{ background: shown.includes(i) ? "var(--zen-text)" : "transparent", color: shown.includes(i) ? "var(--zen-bg)" : "var(--zen-sub)",
                           border: "1px solid var(--zen-border)" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS[timed[i].status].color }} />Day {d.n}
                </button>
              ))}
            </div>
            <button onClick={() => setPage(Math.min(pages - 1, current + 1))} disabled={current >= pages - 1} aria-label="Later days"
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center disabled:opacity-25"
              style={{ border: "1px solid var(--zen-border)" }}><ChevronRight className="w-4 h-4" /></button>
          </div>

          <div ref={grid} className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--zen-border)", background: "var(--zen-s1)" }}>
            <div className="flex sticky top-0 z-10" style={{ background: "var(--zen-s1)", borderBottom: "1px solid var(--zen-border)" }}>
              <div className="shrink-0" style={{ width: AXIS }} />
              {shown.map((i) => (
                <DayHead key={days[i].n} day={days[i]} t={timed[i]} dim={dragging && !fits(dragging, days[i])} onNotes={() => setNotesOf(i)}
                  onAdd={() => setAddTo(i)} />
              ))}
            </div>
            <div className="flex">
              <HourAxis scale={scale} />
              {shown.map((i) => (
                <DayGrid key={days[i].n} di={i} day={days[i]} t={timed[i]} scale={scale} dragging={dragging} over={over === i}
                  onOver={() => setOver(i)} onLeave={() => setOver((o) => (o === i ? null : o))} onDrop={dropOnDay(i)}
                  startDrag={startDrag} endDrag={endDrag} onPick={(place, from) => setMenu({ place, from })}
                  onMeal={(part) => setMealOf({ day: i, part })}
                  onRemove={(place, from) => move(place, from, "tray")} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* bottom bar */}
      <div className="fixed bottom-0 inset-x-0 z-30" style={{ background: "var(--zen-bg)", borderTop: "1px solid var(--zen-border)" }}>
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex items-center gap-2 sm:gap-3">
          <div className="text-[12px] sm:text-[13px] flex-1 min-w-0 leading-snug" style={{ color: "var(--zen-sub)" }}>
            <strong style={{ color: "var(--zen-text)" }}>{days.length} days</strong> ·{" "}
            {attention ? <span style={{ color: "var(--zen-accent)" }}>{attention} need a look</span> : "every day fits"}
            {tray.length ? <> · {tray.length} taken out</> : null}
          </div>
          <button onClick={() => setDrawer(true)} aria-label="Add places" className="lg:hidden zen-btn zen-btn-outline text-sm" style={{ padding: "10px 12px" }}><Plus className="w-4 h-4" /></button>
          <button onClick={reset} aria-label="Start over" className="zen-btn zen-btn-outline text-sm" style={{ padding: "10px 12px" }}><Undo2 className="w-4 h-4 inline sm:mr-1" /><span className="hidden sm:inline">Start over</span></button>
          <button onClick={() => onGenerate?.(days, tray)} className="zen-btn zen-btn-primary text-sm whitespace-nowrap" style={{ padding: "11px 18px" }}>
            <span className="sm:hidden">Generate</span><span className="hidden sm:inline">Generate itinerary</span> <ArrowRight className="w-4 h-4 inline ml-1" />
          </button>
        </div>
      </div>

      {/* a drop on a day of another city bounces, and says why */}
      <AnimatePresence>
        {bounce && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} role="status"
            className="fixed z-40 left-1/2 -translate-x-1/2 bottom-20 max-w-[92vw] w-[460px] rounded-xl px-4 py-3 text-[13px] flex items-start gap-2"
            style={{ background: "var(--zen-text)", color: "var(--zen-bg)" }} data-testid="agenda-bounce">
            <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--zen-accent)" }} />{bounce}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {menu && (
          <Sheet onClose={() => setMenu(null)} title={menu.place.name}>
            <p className="text-[12px] mb-3" style={{ color: "var(--zen-sub)" }}>Move it to</p>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              {days.map((d, di) => (
                <div key={d.n} style={{ opacity: fits(menu.place, d) ? 1 : 0.4 }}>
                  <p className="font-mono-label mb-1.5" style={{ fontSize: 9, color: "var(--zen-dim)" }}>
                    Day {d.n} · {dayLabel(d.date)} · {cityLine(d)}{fits(menu.place, d) ? "" : ` · not in ${menu.place.city}`}
                  </p>
                  {fits(menu.place, d) && (
                    <div className="flex flex-wrap gap-1.5">
                      {d.parts.map((p, pi) => p.places && d.parts.findIndex((q) => q.places && q.key === p.key) === pi && (
                        <button key={pi} onClick={() => move(menu.place, menu.from, { day: di, part: pi })}
                          className="px-3 py-1.5 rounded-full text-[12px]"
                          style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }}>
                          {PARTS[p.key].label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {menu.from !== "tray" && menu.from !== "catalog" && (
              <button onClick={() => move(menu.place, menu.from, "tray")} className="mt-4 w-full zen-btn zen-btn-outline py-2.5 text-sm">
                <X className="w-4 h-4 inline mr-1" />Take it out of my days
              </button>
            )}
          </Sheet>
        )}
        {addTo != null && (
          <Sheet onClose={() => setAddTo(null)} title={`Add to day ${days[addTo].n} · ${cityLine(days[addTo])}`}>
            <NearList day={days[addTo]} t={timed[addTo]} rows={catalog.filter((c) => fits(c, days[addTo]) && c.category !== "food")}
              onChoose={(c) => {
                const best = windows(c.best);
                const at = best && best.length ? best[0][0] : 15 * 60;
                const slot = slotFor(days[addTo], timed[addTo], at);
                if (slot) move(c, "catalog", { day: addTo, ...slot });
                setAddTo(null);
              }} />
          </Sheet>
        )}
        {mealOf != null && (() => {
          const part = timed[mealOf.day].parts[mealOf.part];
          const here = whereAt(timed[mealOf.day], part.at);
          const ours = [...(part.options || [])];
          const near = data.catalog.filter((c) => c.category === "food" && fits(c, days[mealOf.day]) && !ours.some((o) => o.name === c.name));
          return (
            <Sheet onClose={() => setMealOf(null)} title={`${part.key === "lunch" ? "Lunch" : "Dinner"} · Day ${days[mealOf.day].n}${here?.area ? ` · near ${here.area}` : ""}`}>
              <p className="text-[12px] mb-3" style={{ color: "var(--zen-sub)" }}>Tables of ours close to where you are then. Choose one, or keep our pick.</p>
              <NearList day={days[mealOf.day]} t={timed[mealOf.day]} from={here} rows={[...ours.map((o) => ({ ...o, ours: true })), ...near]}
                chosen={(part.chosen || part.options[0])?.name} onChoose={(c) => chooseTable(mealOf, c)} limit={8} />
            </Sheet>
          );
        })()}
        {notesOf != null && (
          <Sheet onClose={() => setNotesOf(null)} title={`Day ${days[notesOf].n} · ${cityLine(days[notesOf])}`}>
            <div className="space-y-1.5">
              {timed[notesOf].notes.map((n, k) => <Warn key={k} text={n} />)}
            </div>
            <p className="text-[12px] mt-3" style={{ color: "var(--zen-sub)" }}>Move a place to a lighter day, or take one out. You can also keep it as it is.</p>
          </Sheet>
        )}
        {drawer && (
          <Sheet onClose={() => setDrawer(false)} title="Add places">
            <Shelf tray={tray} catalog={catalog} toPlace={toPlace} startDrag={startDrag} endDrag={endDrag} onDrop={dropOnTray} over={false} setOver={() => {}}
              onPick={(place, from) => { setDrawer(false); setMenu({ place, from }); }} compact />
          </Sheet>
        )}
      </AnimatePresence>
    </div>
  );
}

// where they are at that hour: the last place they visit before it (its coordinates and area)
function whereAt(t, at) {
  let here = null;
  t.parts.forEach((part) => (part.places || []).forEach((p) => { if (p.start <= at && p.lat) here = p; }));
  return here || t.parts.flatMap((part) => part.places || []).find((p) => p.lat) || null;
}

// our places, nearest first: to where they are then (`from`), or to the closest place already on the day
function NearList({ day, t, rows, from, onChoose, chosen, limit = 30 }) {
  const placed = t.parts.flatMap((part) => part.places || []).filter((p) => p.lat);
  const anchors = from ? [from] : placed;
  const ranked = rows.map((c) => {
    let best = null;
    anchors.forEach((a) => { const d = km(a, c); if (d != null && (best == null || d < best.d)) best = { d, a }; });
    return { c, best };
  }).sort((x, y) => (x.best?.d ?? 99) - (y.best?.d ?? 99)).slice(0, limit);
  if (!ranked.length) return <p className="text-[12px] py-4 text-center" style={{ color: "var(--zen-dim)" }}>Nothing of ours to add here.</p>;
  return (
    <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="agenda-near">
      {ranked.map(({ c, best }) => {
        const leg = best ? ride(best.a, c) : null;
        return (
          <button key={c.id || c.name} onClick={() => onChoose(c)} className="w-full text-left rounded-lg flex overflow-hidden"
            style={{ background: "var(--zen-s1)", border: `1px solid ${chosen === c.name ? "var(--zen-accent)" : "var(--zen-border)"}` }}>
            <div className="w-1 shrink-0" style={{ background: CAT_COLOR[c.category] || CAT_COLOR.popular }} />
            <div className="px-2.5 py-2 min-w-0 flex-1">
              <p className="text-[13px] font-semibold leading-tight">{c.name}{c.ours ? <span className="font-normal" style={{ color: "var(--zen-dim)" }}> · our pick</span> : null}</p>
              <p className="text-[11px] mt-0.5" style={{ color: "var(--zen-sub)" }}>
                {[c.cuisine || CAT_NAME[c.category], c.area,
                  leg ? (leg.method === "walk" ? `${leg.minutes} min walk${best.a.name ? ` from ${best.a.name}` : ""}` : `${leg.minutes} min by train or bus`) : ""]
                  .filter(Boolean).join(" · ")}
              </p>
            </div>
            {chosen === c.name ? <span className="text-[11px] m-2 self-center" style={{ color: "var(--zen-accent)" }}>Chosen</span>
              : <Plus className="w-4 h-4 m-2 shrink-0 self-center" style={{ color: "var(--zen-accent)" }} />}
          </button>
        );
      })}
    </div>
  );
}

// what can be done here, said once and plainly; louder until the first move
function Hint({ strong }) {
  const pill = (Icon, text) => (
    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
      style={{ background: strong ? "var(--zen-text)" : "var(--zen-s1)", color: strong ? "var(--zen-bg)" : "var(--zen-sub)", border: "1px solid var(--zen-border)" }}>
      <Icon className="w-3.5 h-3.5" />{text}
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[12px]" data-testid="agenda-hint">
      {pill(Hand, "Drag or tap a place to move it")}
      {pill(X, "Take it out")}
      {pill(Plus, "Add from our catalog")}
    </div>
  );
}

function DayHead({ day, t, dim, onNotes, onAdd }) {
  const s = STATUS[t.status];
  const pct = Math.min(100, Math.round(t.load * 100));
  return (
    <div className="flex-1 min-w-0 px-2.5 py-2 transition-opacity" style={{ borderLeft: "1px solid var(--zen-border)", opacity: dim ? 0.4 : 1 }}>
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono-label truncate" style={{ fontSize: 9.5, color: "var(--zen-dim)" }}>Day {day.n} · {dayLabel(day.date)}</span>
        {t.notes.length > 0 && (
          <button onClick={onNotes} aria-label="What to look at" className="flex items-center gap-0.5 text-[10px] font-semibold shrink-0"
            style={{ color: "var(--zen-accent)" }}><TriangleAlert className="w-3 h-3" />{t.notes.length}</button>
        )}
      </div>
      <div className="flex items-center justify-between gap-1 mt-0.5">
        <p className="text-[13px] font-semibold truncate">{cityLine(day)}</p>
        <button onClick={onAdd} aria-label={`Add a place to day ${day.n}`} title="Add a place to this day"
          className="w-5 h-5 shrink-0 rounded-full flex items-center justify-center hover:opacity-80"
          style={{ border: "1px solid var(--zen-border-h)", color: "var(--zen-accent)" }}><Plus className="w-3 h-3" /></button>
      </div>
      <div className="w-full rounded-full h-1 mt-1.5" style={{ background: "var(--zen-border-h)" }}>
        <div className="h-1 rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: s.color }} />
      </div>
      <p className="text-[10px] mt-1 font-semibold" style={{ color: t.status === "easy" ? "var(--zen-dim)" : s.color }}>{s.label}</p>
    </div>
  );
}

function HourAxis({ scale }) {
  const hours = [];
  for (let h = scale.from / 60 + 1; h < scale.to / 60; h += 1) hours.push(h);
  return (
    <div className="relative shrink-0" style={{ width: AXIS, height: scale.height }}>
      {hours.map((h) => (
        <span key={h} className="absolute right-2 font-mono-label" style={{ top: scale.y(h * 60) - 6, fontSize: 9, color: "var(--zen-dim)" }}>
          {String(h % 24).padStart(2, "0")}:00
        </span>
      ))}
    </div>
  );
}

// one day: its hours, its places where they fall; while a place is in the hand, the day says if it takes it
function DayGrid({ di, day, t, scale, dragging, over, onOver, onLeave, onDrop, startDrag, endDrag, onPick, onRemove, onMeal }) {
  const yOf = scale.y;
  const ok = dragging ? fits(dragging, day) : null;
  const background = dragging == null ? "transparent"
    : ok ? (over ? "rgba(46,125,50,0.12)" : "rgba(46,125,50,0.05)")
      : "repeating-linear-gradient(135deg, rgba(13,13,13,0.06) 0 6px, transparent 6px 12px)";
  const lines = [];
  for (let h = scale.from / 60 + 1; h < scale.to / 60; h += 1) lines.push(h);
  return (
    <div className="flex-1 min-w-0 relative transition-colors" data-testid={`agenda-day-${day.n}`}
      style={{ height: scale.height, borderLeft: "1px solid var(--zen-border)", background,
               boxShadow: dragging && ok && over ? "inset 0 0 0 2px var(--cat-culture-solid)" : "none" }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = ok ? "move" : "none"; if (!over) onOver(); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) onLeave(); }} onDrop={onDrop}>
      {lines.map((h) => <div key={h} className="absolute inset-x-0" style={{ top: yOf(h * 60), borderTop: "1px solid var(--zen-border)" }} />)}
      {t.parts.map((part, pi) => {
        if (part.key === "travel") {
          return (
            <div key={pi} className="absolute left-1 right-1 rounded-md px-1.5 py-1 overflow-hidden text-[10.5px] leading-tight flex gap-1"
              style={{ top: yOf(part.at), height: Math.max(18, yOf(part.until) - yOf(part.at)), background: "var(--cat-nightlife-solid)", color: "#F5F5F0" }}>
              <TrainFront className="w-3 h-3 shrink-0 mt-px" /><span className="min-w-0">{part.text}</span>
            </div>
          );
        }
        if (part.key === "lunch" || part.key === "dinner") {
          if (part.at == null || (!part.options.length && !part.chosen)) return null;
          const table = part.chosen || part.options[0];
          const others = part.chosen ? 0 : part.options.length - 1;
          return (
            <div key={pi} onClick={() => onMeal(pi)} role="button" data-testid={`agenda-meal-${day.n}-${part.key}`}
              className="absolute left-1 right-1 rounded-md px-1.5 py-0.5 overflow-hidden text-[10.5px] leading-tight flex gap-1 cursor-pointer hover:opacity-90"
              title={`${part.key === "lunch" ? "Lunch" : "Dinner"}: ${table.name}${table.cuisine ? ` · ${table.cuisine}` : ""} — tap to choose where you eat`}
              style={{ top: yOf(part.at), height: Math.max(16, yOf(part.until) - yOf(part.at)), background: "var(--zen-s2)",
                       color: part.warn ? "var(--zen-accent)" : "var(--zen-sub)", border: "1px dashed var(--zen-border-h)" }}>
              <Utensils className="w-3 h-3 shrink-0 mt-px" style={{ color: "var(--cat-foodie-solid)" }} />
              <span className="truncate">{table.name}{others > 0 ? ` +${others}` : ""}</span>
            </div>
          );
        }
        return part.places.map((p, i) => {
          const from = { day: di, part: pi, index: i };
          return (
            <Block key={p.id + i} p={p} top={yOf(p.start)} height={Math.max(18, yOf(p.finish) - yOf(p.start) - 2)}
              onDragStart={startDrag(p, from)} onDragEnd={endDrag} onPick={() => onPick(p, from)} onRemove={() => onRemove(p, from)} />
          );
        });
      })}
      {dragging && !ok && (
        <p className="absolute inset-x-1 top-2 text-center text-[10px] font-semibold" style={{ color: "var(--zen-dim)" }}>
          Only {citiesOf(day).join(" or ")}
        </p>
      )}
    </div>
  );
}

// a place on the calendar: its name, and its hour when there is room; the rest on a tap
function Block({ p, top, height, onDragStart, onDragEnd, onPick, onRemove }) {
  const bad = p.warns.length > 0;
  const tall = height >= 34;
  return (
    <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onPick}
      title={[p.name, `${hm(p.start)}–${hm(p.finish)}`, ...p.warns, ...p.soft].join("\n")}
      className="group absolute left-1 right-1 rounded-md overflow-hidden cursor-grab active:cursor-grabbing transition-shadow hover:shadow-md"
      style={{ top, height, background: "var(--zen-bg)", border: `1px solid ${bad ? "var(--zen-accent)" : "var(--zen-border-h)"}`,
               borderLeft: `3px solid ${CAT_COLOR[p.category] || CAT_COLOR.popular}` }}>
      <div className="pl-1 pr-4 py-0.5">
        <div className="flex items-start gap-0.5">
          <GripVertical className="w-3 h-3 shrink-0 mt-px opacity-40 group-hover:opacity-90" />
          <p className={`text-[11.5px] font-semibold leading-tight min-w-0 ${tall ? "" : "truncate"}`}
            style={tall ? { display: "-webkit-box", WebkitLineClamp: Math.max(1, Math.floor((height - 14) / 14)), WebkitBoxOrient: "vertical", overflow: "hidden" } : {}}>
            {p.name}
          </p>
        </div>
        {tall && <p className="text-[10px] pl-3.5" style={{ color: bad ? "var(--zen-accent)" : "var(--zen-dim)" }}>{hm(p.start)}{bad ? " · check" : ""}</p>}
      </div>
      {bad && !tall && <TriangleAlert className="absolute right-1 top-1 w-3 h-3" style={{ color: "var(--zen-accent)" }} />}
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label={`Take ${p.name} out`}
        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100"
        style={{ background: "var(--zen-text)", color: "var(--zen-bg)" }}><X className="w-2.5 h-2.5" /></button>
    </div>
  );
}

const Warn = ({ text }) => (
  <p className="text-[12px] flex items-start gap-1.5 leading-snug" style={{ color: "var(--zen-accent)" }}>
    <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-px" />{text}
  </p>
);

// ── the shelf: set aside, and the catalog ───────────────────────────────────
function Shelf({ tray, catalog, toPlace = [], startDrag, endDrag, onDrop, over, setOver, onPick, compact }) {
  const [tab, setTab] = useState(toPlace.length ? "toPlace" : "catalog");
  const [q, setQ] = useState("");
  const list = tab === "tray" ? tray : tab === "toPlace" ? toPlace : catalog.filter((c) => !q || `${c.name} ${c.city} ${c.area}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver("tray"); }} onDragLeave={() => setOver(null)} onDrop={onDrop}
      className={compact ? "" : "rounded-2xl p-4"}
      style={compact ? {} : { background: "var(--zen-s1)", border: `1.5px ${over ? "dashed var(--zen-accent)" : "solid var(--zen-border)"}` }}>
      <div className="flex flex-wrap gap-1 mb-3">
        {[...(toPlace.length ? [["toPlace", `To place (${toPlace.length})`]] : []), ["catalog", "Add from catalog"], ["tray", `Taken out (${tray.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className="flex-1 px-2 py-1.5 rounded-full text-[12px]"
            style={{ background: tab === k ? "var(--zen-text)" : "transparent", color: tab === k ? "var(--zen-bg)" : "var(--zen-sub)", border: "1px solid var(--zen-border)" }}>
            {label}
          </button>
        ))}
      </div>
      {tab === "catalog" && (
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--zen-dim)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search places"
            className="w-full rounded-lg pl-8 pr-3 py-2 text-[13px] outline-none"
            style={{ background: "var(--zen-bg)", border: "1px solid var(--zen-border)", color: "var(--zen-text)" }} />
        </div>
      )}
      <p className="text-[11px] mb-2" style={{ color: "var(--zen-dim)" }}>
        {tab === "tray" ? "Drag a place here to take it out of your days." : tab === "toPlace" ? "In your trip, not on a day yet: drag it onto one, or tap to choose." : "Drag onto a day, or tap to choose where."}
      </p>
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
        {list.length === 0 && <p className="text-[12px] py-4 text-center" style={{ color: "var(--zen-dim)" }}>{tab === "tray" ? "Nothing taken out." : "No match."}</p>}
        {list.map((p) => (
          <div key={p.id} draggable onDragStart={startDrag(p, tab)} onDragEnd={endDrag} onClick={() => onPick(p, tab)}
            className="rounded-lg flex overflow-hidden cursor-grab" style={{ background: "var(--zen-bg)", border: "1px solid var(--zen-border)" }}>
            <div className="w-1 shrink-0" style={{ background: CAT_COLOR[p.category] || CAT_COLOR.popular }} />
            <div className="px-2.5 py-2 min-w-0 flex-1">
              <p className="text-[12px] font-semibold leading-tight">{p.name}</p>
              <p className="text-[10px] mt-0.5" style={{ color: "var(--zen-sub)" }}>
                {CAT_NAME[p.category] || "Place"} · {p.city}{p.minutes ? ` · ${dur(p.minutes)}` : ""}
              </p>
            </div>
            <Plus className="w-4 h-4 m-2 shrink-0" style={{ color: "var(--zen-accent)" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Sheet({ title, onClose, children }) {
  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0" style={{ background: "rgba(13,13,13,0.35)" }} onClick={onClose} />
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="relative w-full sm:w-[440px] rounded-t-2xl sm:rounded-2xl p-5" style={{ background: "var(--zen-bg)", border: "1px solid var(--zen-border)" }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-heading text-[15px] tracking-tight">{title}</h3>
          <button onClick={onClose} aria-label="Close"><X className="w-4 h-4" style={{ color: "var(--zen-sub)" }} /></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

// ── free: the shape of their days, the detail locked (docs/producto/MODELO_PRODUCTO_Y_ECONOMICO.md 2.1) ──
// The areas of each day, how many places, near where they eat: what tells it is already organised for them.
// No name, no hour, no order — the server never sends them (backend agents/agenda.shape_of).
export function AgendaShape({ shape, onUnlock, onBack }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--zen-bg)", color: "var(--zen-text)" }}>
      <BrandHeader />
      <div className="px-4 md:px-8 pt-6 pb-28 max-w-[1200px] w-full mx-auto">
        {onBack && <button onClick={onBack} className="text-sm mb-3 hover:opacity-70" style={{ color: "var(--zen-sub)" }}>← Back to your places</button>}
        <p className="font-mono-label mb-1" style={{ fontSize: 10, color: "var(--zen-accent)" }}>Your days</p>
        <h1 className="font-heading text-2xl md:text-3xl tracking-tight">{shape.trip?.title || "Your trip"}</h1>
        <p className="text-[14px] mt-2 max-w-xl" style={{ color: "var(--zen-sub)" }}>
          Your days are already organised: each one in areas a short walk apart, with lunch and dinner close to where you are.
          Unfold them to see every place and hour, and shape them by hand.
        </p>
        <div className="grid gap-3 mt-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }} data-testid="agenda-shape">
          {shape.days.map((d) => (
            <div key={d.n} className="rounded-xl p-3" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }}>
              <p className="font-mono-label" style={{ fontSize: 9.5, color: "var(--zen-dim)" }}>Day {d.n} · {dayLabel(d.date)}</p>
              <p className="text-[14px] font-semibold mt-0.5">{d.city}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {d.areas.map((a) => (
                  <span key={a} className="px-2 py-0.5 rounded-full text-[11px]" style={{ background: "var(--zen-bg)", border: "1px solid var(--zen-border)" }}>{a}</span>
                ))}
                {d.travel && <span className="px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1" style={{ background: "var(--cat-nightlife-solid)", color: "#F5F5F0" }}><TrainFront className="w-3 h-3" />On the move</span>}
              </div>
              {/* the places, as their outline only */}
              <div className="mt-3 space-y-1.5" aria-hidden>
                {Array.from({ length: Math.min(d.places, 6) }).map((_, k) => (
                  <div key={k} className="h-3 rounded" style={{ background: "var(--zen-border-h)", width: `${60 + ((k * 23) % 35)}%`, opacity: 0.7 }} />
                ))}
              </div>
              <p className="text-[11px] mt-2" style={{ color: "var(--zen-sub)" }}>{d.places} place{d.places === 1 ? "" : "s"}</p>
              {d.meals.map((m, k) => (
                <p key={k} className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: "var(--zen-sub)" }}>
                  <Utensils className="w-3 h-3" style={{ color: "var(--cat-foodie-solid)" }} />
                  {m.kind === "lunch" ? "Lunch" : "Dinner"}{m.near ? ` near ${m.near}` : ""}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="fixed bottom-0 inset-x-0 z-30" style={{ background: "var(--zen-bg)", borderTop: "1px solid var(--zen-border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-3 flex items-center gap-3">
          <p className="text-[13px] flex-1" style={{ color: "var(--zen-sub)" }}>Every place, every hour, and your days to shape.</p>
          <button onClick={onUnlock} className="zen-btn zen-btn-primary text-sm whitespace-nowrap" style={{ padding: "11px 18px" }} data-testid="agenda-unlock">
            Unfold my days <ArrowRight className="w-4 h-4 inline ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}
